import { TableColumnItem } from 'src/app/components/table-dashboard-item/table-dashboard-item.component';
import { CampaignTableDashoardItemHelper } from 'src/app/dashboards/mopa-campaigns/items/campaigns-table';
import { SkanTableDashoardItemHelper } from 'src/app/dashboards/mopa-skadnetwork/item/skan-table';
import { datePartDateTrunc, datetimePartDateTrunc } from 'src/app/utils/sql';
import { IDatasourceColumnSQLMap } from '../../../utils/datasource-sql-map/datasource';

export enum QueryContextSettingID {
    ABTESTS_MAX_DAY_NO = 'abtests.maxDayNo',
    ABTESTS_UPLIFT_REFERENCE = 'abtests.upliftReference',
    CAMPAIGN_ACTIVE_ALL = 'campaign.activeAll',
    CAMPAIGNS_SELECTED_METRICS = 'campaigns.selectedMetrics',
    CUSTOM_FUNNELS_OPEN_CLOSED = 'customfunnels.openClosed',
    DATE_AGGREGATION = 'dateAggregation',
    DATETIME_AGGREGATION = 'datetimeAggregation',
    FUNNELS_LIMIT_DETAIL_STEPS = 'funnels.limitDetailSteps',
    FUNNELS_STEP_COUNT_STRATEGY = 'funnels.stepCountStrategy',
    FUNNELS_OTHER_GROUPING_STRATEGY = 'funnels.otherGroupingStrategy',
    NET_GROSS = 'netGross',
    CURRENCY = 'currency',
    REALTIME_CLOUDSAVE_NORMALIZE_METRICS = 'cloudsave.normalizeMetrics',
    REALTIME_HOURS_UP_TO_LATEST = 'realtimeHoursUpToLatest',
    REALTIME_NORMALIZE_METRICS = 'realtimeNormaliseMetrics',
    REALTIME_CURRENT_WEEKDAY = 'realtimeCurrentWeekday',
    REVENUE_TYPE = 'revenueType',
    ROAS_SELECTED_DAY_NO = 'roas.selectedDayNo',
    SHOW_TOTAL_REVENUE = 'showTotalRevenue',
    SHOW_INAPP_REVENUE = 'showInappRevenue',
    SHOW_AD_REVENUE = 'showAdRevenue',
    SKADNETWORK_SELECTED_METRICS = 'skadnetwork.selectedMetrics',
    RETENTION_SELECTED_DAY_NO = 'retention.selectedDayNo',
    RETENTION_MAX_DAY_NO = 'retention.maxDayNo',
    ENGAGEMENT_MAX_DAY_NO = 'engagement.maxDayNo',
    ENGAGEMENT_SELECTED_DAY_AND_SESSION = 'engagement.selectedDayAndSession',
    META_DAY_MODE = 'meta-engagement.dayMode',
    META_SHOW_ALL_USERS = 'meta-showAllUsers',
    META_SHOW_META_USERS = 'meta-showMetaUsers',
    META_SHOW_HOPA_USERS = 'meta-showHopaUsers',
    META_SHOW_META_PRODUCTIVE_USERS = 'meta-showMetaProductiveUsers',
    META_SHOW_CORE_LOOP_USERS = 'meta-showCoreLoopUsers',
    FLOW_VALUE_MODE = 'flow.valueMode',
    FLOW_POPULATION = 'flow.population',
    FLOW_X_AXIS = 'flow.xAxis',
    FLOW_ACCUMULATION = 'flow.accumulation',
    FLOW_USER_TYPE = 'flow.userType',
    SHOW_PERCENTILE_50 = 'showPercentile50',
    SHOW_PERCENTILE_75 = 'showPercentile75',
    SHOW_PERCENTILE_90 = 'showPercentile90',
    SHOW_PERCENTILE_99 = 'showPercentile99',
    RESOURCE_FLOW_CURRENCY = 'resourceFlow.currency',
}

export interface IQueryContextSetting {
    id: QueryContextSettingID;
    value: any;
}

export interface IQueryContextDatasourceColumnState {
    value: any;
    isFilterApplied: boolean;
    isSplitApplied: boolean;
    /**
     * Opt-in: this column is the breakdown "bucket"/group dimension (parallel to split). Consumed by
     * composite dashboards to group their breakdown/chart under this dimension. Optional → backward
     * compatible; absent means not bucketed.
     */
    isBucketApplied?: boolean;
}

export interface IQueryContextDatasourceColumn {
    id: IDatasourceColumnSQLMap['columnId'];

    /**
     * State of a controller.
     * if defined statically in dashboard it can be initial state
     * TODO in future there should be an async function to get those state from i.e. local cache, firestore
     */
    state: IQueryContextDatasourceColumnState;
}

/**
 * An interface representing atomic (single) change in query context.
 */
export interface IQueryContextChange {
    setting?: IQueryContextSetting;
    datasourceColumn?: IQueryContextDatasourceColumn;
}

/**
 * An interface representing query context.
 * Contains all selected settings and used datasource columns. Related to query.
 * TODO: Refactor structure so that datasourceColumns are divided into filters and splits.
 */
export interface IQueryContext {
    settings: {
        [QueryContextSettingID.ABTESTS_MAX_DAY_NO]?: string;
        [QueryContextSettingID.ABTESTS_UPLIFT_REFERENCE]?: string;
        [QueryContextSettingID.CAMPAIGN_ACTIVE_ALL]?: 'active' | 'all';
        [QueryContextSettingID.CAMPAIGNS_SELECTED_METRICS]?: string[];
        [QueryContextSettingID.CURRENCY]?: 'usd' | 'pln';
        [QueryContextSettingID.CUSTOM_FUNNELS_OPEN_CLOSED]?: 'open' | 'closed';
        [QueryContextSettingID.NET_GROSS]?: 'net' | 'gross';
        [QueryContextSettingID.DATE_AGGREGATION]?: datePartDateTrunc;
        [QueryContextSettingID.DATETIME_AGGREGATION]?: datetimePartDateTrunc;
        [QueryContextSettingID.ENGAGEMENT_MAX_DAY_NO]?: number;
        [QueryContextSettingID.ENGAGEMENT_SELECTED_DAY_AND_SESSION]?: number[];
        [QueryContextSettingID.FUNNELS_LIMIT_DETAIL_STEPS]?: string;
        [QueryContextSettingID.FUNNELS_STEP_COUNT_STRATEGY]?:
            | 'master_step_cnt'
            | 'total_cnt';
        [QueryContextSettingID.FUNNELS_OTHER_GROUPING_STRATEGY]?:
            | 'raw'
            | 'dynamic'
            | 'master_step'
            | 'adventure';
        [QueryContextSettingID.REALTIME_CLOUDSAVE_NORMALIZE_METRICS]?:
            | 'none'
            | 'dau'
            | 'non_anonymous';
        [QueryContextSettingID.REALTIME_HOURS_UP_TO_LATEST]?: boolean;
        [QueryContextSettingID.REALTIME_NORMALIZE_METRICS]?: 'none' | 'dau';
        [QueryContextSettingID.REALTIME_CURRENT_WEEKDAY]?:
            | boolean
            | 'Monday'
            | 'Tuesday'
            | 'Wednesday'
            | 'Thursday'
            | 'Friday'
            | 'Saturday'
            | 'Sunday';
        [QueryContextSettingID.RETENTION_SELECTED_DAY_NO]?: (
            | '1'
            | '3'
            | '7'
            | '14'
            | '30'
            | '90'
            | '180'
            | '360'
        )[];
        [QueryContextSettingID.RETENTION_MAX_DAY_NO]?:
            | '60'
            | '90'
            | '180'
            | '360'
            | '720'
            | 'unlimited';
        [QueryContextSettingID.REVENUE_TYPE]?: 'total' | 'inapp' | 'ad';
        [QueryContextSettingID.ROAS_SELECTED_DAY_NO]?: (
            | '1'
            | '3'
            | '7'
            | '14'
            | '30'
            | '90'
            | '180'
            | '360'
        )[];
        [QueryContextSettingID.SHOW_TOTAL_REVENUE]?: boolean;
        [QueryContextSettingID.SHOW_INAPP_REVENUE]?: boolean;
        [QueryContextSettingID.SHOW_AD_REVENUE]?: boolean;
        [QueryContextSettingID.SKADNETWORK_SELECTED_METRICS]?: string[];
        [QueryContextSettingID.META_DAY_MODE]?: 'active_day' | 'install_day';
        [QueryContextSettingID.META_SHOW_ALL_USERS]?: boolean;
        [QueryContextSettingID.META_SHOW_META_USERS]?: boolean;
        [QueryContextSettingID.META_SHOW_HOPA_USERS]?: boolean;
        [QueryContextSettingID.META_SHOW_META_PRODUCTIVE_USERS]?: boolean;
        [QueryContextSettingID.META_SHOW_CORE_LOOP_USERS]?: boolean;
        [QueryContextSettingID.FLOW_VALUE_MODE]?:
            | 'total'
            | 'average'
            | 'percentile'
            | 'flow records'
            | 'players';
        [QueryContextSettingID.FLOW_POPULATION]?: 'dau' | 'deu';
        [QueryContextSettingID.FLOW_X_AXIS]?:
            | 'date'
            | 'install_day'
            | 'active_day';
        [QueryContextSettingID.FLOW_ACCUMULATION]?: 'daily' | 'running';
        [QueryContextSettingID.FLOW_USER_TYPE]?: 'all' | 'hopa' | 'meta';
        [QueryContextSettingID.SHOW_PERCENTILE_50]?: boolean;
        [QueryContextSettingID.SHOW_PERCENTILE_75]?: boolean;
        [QueryContextSettingID.SHOW_PERCENTILE_90]?: boolean;
        [QueryContextSettingID.SHOW_PERCENTILE_99]?: boolean;
        [QueryContextSettingID.RESOURCE_FLOW_CURRENCY]?:
            | 'energy'
            | 'gems'
            | 'hints'
            | 'coins'
            | 'compasses';
        // [key: string]: any;
    };
    datasourceColumns: IQueryContextDatasourceColumn[];
}

// Define default settings for query context
// export const QueryContextDefaultSettings: Partial<IQueryContext['settings']> = {
export const QueryContextDefaultSettings = {
    [QueryContextSettingID.ABTESTS_MAX_DAY_NO]: '30',
    [QueryContextSettingID.CAMPAIGNS_SELECTED_METRICS]:
        CampaignTableDashoardItemHelper.getAllDefaultMetrics()
            .map((metric: TableColumnItem) => metric.id)
            .filter((metricId: string) => {
                // exclude all that starts with 'arpu' or 'cpt' or 'roas_w' or 'roas_m' or 'retention_d0'
                return (
                    !metricId.startsWith('arpu') &&
                    !metricId.startsWith('cpt') &&
                    !metricId.startsWith('roas_w') &&
                    !metricId.startsWith('roas_m') &&
                    !metricId.startsWith('revenue') &&
                    !metricId.startsWith('running_revenue') &&
                    metricId !== 'retention_d0'
                );
            }),
    [QueryContextSettingID.ENGAGEMENT_MAX_DAY_NO]: '30',
    [QueryContextSettingID.ENGAGEMENT_SELECTED_DAY_AND_SESSION]: [3],
    [QueryContextSettingID.FUNNELS_LIMIT_DETAIL_STEPS]: '500',
    [QueryContextSettingID.FUNNELS_OTHER_GROUPING_STRATEGY]: 'raw' as const,
    [QueryContextSettingID.FUNNELS_STEP_COUNT_STRATEGY]:
        'master_step_cnt' as const,
    [QueryContextSettingID.REALTIME_NORMALIZE_METRICS]: 'none' as const,
    [QueryContextSettingID.RETENTION_MAX_DAY_NO]: '180',
    [QueryContextSettingID.RETENTION_SELECTED_DAY_NO]: ['1', '7', '30'],
    [QueryContextSettingID.SKADNETWORK_SELECTED_METRICS]:
        SkanTableDashoardItemHelper.tableSkanPostbacksColumnsGetter().map(
            (metric: TableColumnItem) => metric.id
        ),
};

/**
 *  Checks if any split is applied in query context
 * @param context
 * @returns boolean
 *
 */
export function isSplitApplied(context: IQueryContext): boolean {
    return context.datasourceColumns.some(
        (column) => column.state.isSplitApplied
    );
}
